// dashboard/src/hooks/useAuth.tsx

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import {
  signIn,
  signUp,
  signOut,
  signInWithRedirect,
  confirmSignUp as amplifyConfirmSignUp,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
} from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import { isCognitoConfigured, isGoogleOAuthConfigured } from '@/config/aws'

interface User {
  id: string
  email: string
  name: string
  tenantId?: string
  role?: string
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isSuperAdmin: boolean
  isLoading: boolean
  error: string | null
  googleOAuthAvailable: boolean
  login: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  register: (email: string, password: string, name: string) => Promise<void>
  logout: () => Promise<void>
  confirmSignUp: (email: string, code: string) => Promise<boolean>
  forgotPassword: (email: string) => Promise<void>
  resetPasswordConfirm: (email: string, code: string, newPassword: string) => Promise<void>
  clearError: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const DEMO_EMAIL = import.meta.env.VITE_DEMO_EMAIL || ''
const DEMO_PASSWORD = import.meta.env.VITE_DEMO_PASSWORD || ''
const DEMO_TENANT_ID = import.meta.env.VITE_DEMO_TENANT_ID || ''
const SUPER_ADMIN_EMAILS: string[] = (import.meta.env.VITE_SUPER_ADMIN_EMAIL || '')
  .toLowerCase()
  .split(',')
  .map((e: string) => e.trim())
  .filter(Boolean)

export function checkIsSuperAdmin(email?: string): boolean {
  if (!email) return false
  // If no super admin emails configured and Cognito isn't set up (demo mode), treat user as super admin
  if (SUPER_ADMIN_EMAILS.length === 0) return !isCognitoConfigured()
  return SUPER_ADMIN_EMAILS.includes(email.toLowerCase())
}

// Demo user for when Cognito is not configured
const DEMO_USER: User = {
  id: 'demo-user',
  email: DEMO_EMAIL,
  name: 'Demo User',
  tenantId: DEMO_TENANT_ID,
  role: checkIsSuperAdmin(DEMO_EMAIL) ? 'super_admin' : 'admin'
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  const cognitoEnabled = isCognitoConfigured()

  // Check if user is authenticated on mount + listen for OAuth redirect completion
  useEffect(() => {
    checkAuthState()

    // Listen for Amplify Hub auth events (OAuth redirect completion)
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        // OAuth redirect completed successfully — re-check auth state
        checkAuthState()
      }
      if (payload.event === 'signInWithRedirect_failure') {
        console.error('Google sign-in redirect failed:', payload.data)
        const errorMsg = String(payload.data || '')
        if (errorMsg.includes('Registration required') || errorMsg.includes('PreSignUp')) {
          setError('You need to register first. Please create an account with email and password, verify your email, then you can sign in with Google.')
        } else {
          setError('Google sign-in failed. Please try again.')
        }
        setIsLoading(false)
      }
    })

    return () => unsubscribe()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function checkAuthState() {
    try {
      setIsLoading(true)

      // If Cognito not configured, use demo mode
      if (!cognitoEnabled) {
        console.warn('Cognito not configured - running in demo mode')
        // Check localStorage for demo session
        const demoSession = localStorage.getItem('snatched_demo_session')
        if (demoSession && DEMO_EMAIL) {
          setUser(DEMO_USER)
        }
        setIsLoading(false)
        return
      }

      // Try standard Cognito GetUser path first
      try {
        const currentUser = await getCurrentUser()
        const attributes = await fetchUserAttributes()
        setUser({
          id: currentUser.userId,
          email: attributes.email || '',
          name: attributes.name || attributes.email || '',
          tenantId: attributes['custom:tenant_id'],
          role: attributes['custom:role'],
        })
        return
      } catch {
        // GetUser may fail for OAuth users if access token lacks aws.cognito.signin.user.admin scope
        // Fall back to reading claims from the ID token
      }

      // Fallback: extract user info from ID token claims
      const session = await fetchAuthSession()
      const idToken = session.tokens?.idToken
      if (idToken) {
        const claims = idToken.payload
        setUser({
          id: (claims.sub as string) || '',
          email: (claims.email as string) || '',
          name: (claims.name as string) || (claims.email as string) || '',
          tenantId: claims['custom:tenant_id'] as string | undefined,
          role: claims['custom:role'] as string | undefined,
        })
      } else {
        setUser(null)
      }
    } catch (error) {
      console.log('Auth check failed:', error instanceof Error ? error.message : 'Unknown error')
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  async function login(email: string, password: string) {
    try {
      setError(null)
      setIsLoading(true)
      
      // Demo mode
      if (!cognitoEnabled) {
        if (!DEMO_EMAIL || !DEMO_PASSWORD) {
          throw new Error('Demo credentials not configured for this environment.')
        }
        if (email === DEMO_EMAIL && password === DEMO_PASSWORD) {
          localStorage.setItem('snatched_demo_session', 'true')
          setUser(DEMO_USER)
          return
        }
        throw new Error('Invalid demo credentials.')
      }
      
      const result = await signIn({ username: email, password })
      
      if (result.isSignedIn) {
        await checkAuthState()
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        throw new Error('CONFIRM_SIGN_UP')
      }
    } catch (err: any) {
      const message = err.message || 'Error signing in'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  async function handleSignInWithGoogle() {
    try {
      setError(null)
      if (!cognitoEnabled || !isGoogleOAuthConfigured()) {
        throw new Error('Google sign-in is not configured')
      }
      // Clear any existing/stale session to avoid "There is already a signed in user" error
      try { await signOut() } catch { /* ignore — no session to clear */ }
      await signInWithRedirect({ provider: 'Google' })
    } catch (err: any) {
      const message = err.message || 'Error signing in with Google'
      setError(message)
      throw err
    }
  }

  async function register(email: string, password: string, name: string) {
    try {
      setError(null)
      setIsLoading(true)
      
      // Demo mode
      if (!cognitoEnabled) {
        throw new Error('Registration disabled in demo mode.')
      }
      
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            name,
          },
        },
      })
    } catch (err: any) {
      const message = err.message || 'Error registering'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  async function confirmSignUpCode(email: string, code: string): Promise<boolean> {
    try {
      setError(null)
      setIsLoading(true)

      if (!cognitoEnabled) {
        throw new Error('Not available in demo mode')
      }

      const { isSignUpComplete } = await amplifyConfirmSignUp({
        username: email,
        confirmationCode: code,
      })

      return isSignUpComplete
    } catch (err: any) {
      const message = err.message || 'Error verifying code'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  async function forgotPassword(email: string) {
    try {
      setError(null)
      setIsLoading(true)
      
      if (!cognitoEnabled) {
        throw new Error('Not available in demo mode')
      }
      
      await resetPassword({ username: email })
    } catch (err: any) {
      const message = err.message || 'Error sending code'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  async function resetPasswordConfirm(email: string, code: string, newPassword: string) {
    try {
      setError(null)
      setIsLoading(true)
      
      if (!cognitoEnabled) {
        throw new Error('Not available in demo mode')
      }
      
      await confirmResetPassword({ 
        username: email, 
        confirmationCode: code, 
        newPassword 
      })
    } catch (err: any) {
      const message = err.message || 'Error resetting password'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  async function logout() {
    try {
      setIsLoading(true)
      
      if (!cognitoEnabled) {
        localStorage.removeItem('snatched_demo_session')
        setUser(null)
        return
      }
      
      await signOut()
      setUser(null)
    } catch (err: any) {
      console.error('Logout error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  function clearError() {
    setError(null)
  }

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isSuperAdmin: checkIsSuperAdmin(user?.email),
    isLoading,
    error,
    googleOAuthAvailable: cognitoEnabled && isGoogleOAuthConfigured(),
    login,
    signInWithGoogle: handleSignInWithGoogle,
    register,
    logout,
    confirmSignUp: confirmSignUpCode,
    forgotPassword,
    resetPasswordConfirm,
    clearError,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Hook for getting auth token
export async function getAuthToken(): Promise<string | null> {
  try {
    if (!isCognitoConfigured()) {
      return 'demo-token'
    }
    const session = await fetchAuthSession()
    // Use idToken for API Gateway Cognito Authorizer (not accessToken)
    return session.tokens?.idToken?.toString() || null
  } catch {
    return null
  }
}
